/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, action, computed } from '@ember/object';
import { setDiff, intersect, filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import LineItemAdjustmentMode from 'linced-app/enums/line-item-adjustment-mode';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import { matchYearLevel } from 'linced-app/utils/year-levels';
import * as arrays from '@linc-technologies/linced-common/utils/arrays';

export default class ItemController extends Controller {
  LineItemAdjustmentMode = LineItemAdjustmentMode;
  matchYearLevel = matchYearLevel;

  @service
  fetch;

  @service
  session;

  @service
  features;

  @service
  finance;

  @service
  personOptions;

  @service
  strings;

  @tracked hideArchivedLineItems = false;
  @tracked groupFilter = [];

  @computed('model.groups.[]')
  get filteredGroups() {
    return this.model.groups
      .filter((group) => {
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!staff.find(
            (item) => item.personId === this.session.personId,
          );
        }
        if (group.status === NSWStatus.INACTIVE) {
          return false;
        }
        // Include all other groups.
        return true;
      })
      .sort(arrays.byKeys('type', 'label'));
  }

  @setDiff('filteredGroups', 'groupFilter')
  remainingGroups;

  @computed('remainingGroups')
  get sortedRemainingGroups() {
    let groupTypes = arrays
      .uniq(this.remainingGroups.map((item) => item.type))
      .sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = this.remainingGroups.filter(
        (item) => item.type === groupType,
      );
      let groupings = arrays
        .uniq(semiFilteredGroups.map((item) => item.grouping))
        .sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...arrays
            .filterBy(semiFilteredGroups, 'grouping', grouping)
            .sort(naturalSort('label')),
        );
      });
    });
    return sortedRemainingGroups;
  }

  groupFilterLabel = 'Filter by Group';

  @alias('session.school')
  school;

  @tracked yearLevelFilter = [];

  @setDiff('school.yearLevels', 'yearLevelFilter')
  remainingYearLevels;

  @filterBy('model.lineItems', 'isCommunity', true)
  communityItems;

  @filterBy('model.lineItems', 'isCommunity', false)
  learnerItems;

  @computed('school.jurisdiction', 'model.ledgerItem.isRefundable')
  get itemIsRefundable() {
    switch (this.get('school.jurisdiction')) {
      case Jurisdiction.AU_NSW:
        return this.get('model.ledgerItem.isRefundable');
    }

    // By default, all items are refundable.
    return true;
  }

  @computed('communityItems.[]', 'hideArchivedLineItems')
  get filteredCommunityItems() {
    if (this.hideArchivedLineItems) {
      return this.communityItems.filter((item) => !item.isArchived);
    }
    return this.communityItems;
  }

  @computed(
    'learnerItems.[]',
    'groupFilter.[]',
    'yearLevelFilter.[]',
    'model.bulkWriteoff',
    'enrolmentStatuses.[]',
    'hideArchivedLineItems',
  )
  get filteredLineItems() {
    let items = [...this.learnerItems];
    if (this.hideArchivedLineItems) {
      items = items.filter((item) => !item.isArchived);
    }
    if (this.enrolmentStatuses.length < 2) {
      // Filter by Enrolment Status firstest
      items = items.filter((item) => {
        return this.enrolmentStatuses.includes(
          item.get('account.person.enrolmentStatus'),
        );
      });
    }
    // Filter by year level first, to reduce the number of iterations in group filtering (if applicable).
    if (this.yearLevelFilter.length > 0) {
      items = items.filter((item) => {
        let currentYearLevel = '' + item.get('account.person.currentYearLevel');
        return this.yearLevelFilter.includes(currentYearLevel);
      });
    }
    if (this.groupFilter.length > 0) {
      items = items.filter((item) => {
        let personId = item.get('account.person.id');
        return this.groupFilter.some((group) => {
          return group.currentLearners.find(
            (item) => item.personId === personId,
          );
        });
      });
    }
    if (this.model.bulkWriteoff) {
      items = items.filter((item) => {
        return item.balance > 0;
      });
    }

    return items;
  }

  @tracked
  selectedLineItems = [];

  @intersect('selectedLineItems', 'filteredLineItems')
  filteredSelectedLineItems;

  @tracked
  refundDialogOpen = false;

  @tracked
  refundInProgress = false;

  @tracked
  refundType = 'direct';

  @tracked
  refundAmount = 0;

  @action
  setRefundAmount(value) {
    this.refundAmount = value;
  }

  @tracked
  refundComment = '';

  @action
  setRefundComment(value) {
    this.refundComment = value;
  }

  @tracked
  refundDate = moment().format('YYYY-MM-DD');

  @action
  setRefundDate(value) {
    this.refundDate = value;
  }

  @tracked
  transactionsLoading = false;

  @computed(
    'filteredSelectedLineItems.[]',
    'model.transactions.[]',
    'transactionsLoading',
  )
  get refundRows() {
    let ids = this.filteredSelectedLineItems.map((item) => item.id);
    let amounts = this.model.transactions.reduce((amounts, transaction) => {
      if (!ids.includes(transaction.line_item_id)) {
        // Not filtered/selected; ignore.
        return amounts;
      }
      let amount = get(amounts, transaction.line_item_id) ?? 0;

      switch (transaction.type) {
        case TransactionType.PAYMENT:
        case TransactionType.TRANSFER:
        case TransactionType.REFUND:
        case TransactionType.APPORTIONMENT:
          amounts[transaction.line_item_id] =
            Number(amount) + Number(transaction.displayAmount);
          break;
      }

      return amounts;
    }, {});

    return this.filteredSelectedLineItems.map((item) => {
      return {
        itemId: item.id,
        item,
        refundable: get(amounts, item.id),
      };
    });
  }

  @computed(
    'refundRows.[]',
    'model.{ledgerItem.defaultAmount,ledgerItems.@each.defaultAmount,bulkWriteoff}',
  )
  get maxRefundAmount() {
    let amounts = arrays.compact(
      this.refundRows.map((item) => item.refundable),
    );
    let defaultAmount = 0;
    if (this.model.bulkWriteoff) {
      defaultAmount = Math.max(
        ...this.model.ledgerItems.map((item) => item.defaultAmount),
      );
    } else {
      defaultAmount = this.get('model.ledgerItem.defaultAmount');
    }
    return Math.max(defaultAmount, ...amounts).toFixed(2);
  }

  @tracked refundsToProcess = 0;
  @tracked refunds = [];
  @tracked
  refundsConfirmed = false;
  @tracked refundsProcessed = 0;
  @tracked refundsFailed = 0;
  @tracked refundErrors = [];

  get refundsProgress() {
    if (this.refundsToProcess > 0) {
      return (this.refundsProcessed / this.refundsToProcess) * 100;
    }
    return 0;
  }

  @tracked
  cancellationDialogOpen = false;

  @tracked
  cancellationInProgress = false;

  @tracked
  cancellationType = 'writeoff';

  @action
  setCancellationType(value) {
    this.cancellationType = value;
  }

  @tracked
  cancellationComment = '';

  @action
  setCancellationComment(value) {
    this.cancellationComment = value;
  }

  @tracked
  cancellationDate = moment().format('YYYY-MM-DD');

  @action
  setCancellationDate(value) {
    this.cancellationDate = value;
  }

  get cancellationCommentLabel() {
    switch (this.cancellationType) {
      case 'writeoff':
        return 'write off';
      case 'void':
        return 'void';
    }
    return 'adjustment';
  }

  @computed('filteredSelectedLineItems.@each.balance')
  get cancellationRows() {
    return this.filteredSelectedLineItems.filter((item) => item.balance);
  }

  @computed('cancellationRows.@each.{balance,displayAmount}')
  get cancellationCanVoid() {
    // Full voids are only possible if no items have any credits.
    return this.cancellationRows.every(
      (item) => item.balance === item.displayAmount,
    );
  }

  @tracked
  cancellationsProcessed = 0;

  @tracked
  cancellationsFailed = 0;

  @tracked
  cancellationErrors = [];

  @computed('cancellationRows.length', 'cancellationsProcessed')
  get cancellationProgress() {
    if (this.cancellationRows.length > 0) {
      return (this.cancellationsProcessed / this.cancellationRows.length) * 100;
    }
    return 0;
  }

  @tracked
  adjustmentMode = LineItemAdjustmentMode.TO;

  @action
  setAdjustmentMode(value) {
    this.adjustmentMode = value;
  }

  @tracked
  adjustmentsInProgress = false;

  @tracked
  adjustBy = 0;

  @action
  setAdjustBy(value) {
    this.adjustBy = value;
  }

  @tracked
  adjustTo = 0;

  @action
  setAdjustTo(value) {
    this.adjustTo = value;
  }

  @tracked
  adjustmentComment = '';

  @action
  setAdjustmentComment(value) {
    this.adjustmentComment = value;
  }

  @tracked
  adjustmentDate = moment().format('YYYY-MM-DD');

  @action
  setAdjustmentDate(value) {
    this.adjustmentDate = value;
  }

  @tracked
  adjustmentsToProcess = 0;

  @tracked
  adjustmentsProcessed = 0;

  @tracked
  adjustmentsFailed = 0;

  @tracked
  adjustmentErrors = [];

  @tracked
  increaseDialogOpen = false;

  @computed('filteredSelectedLineItems.@each.displayAmount')
  get minIncreaseTo() {
    return Math.max(
      ...this.filteredSelectedLineItems.map((item) => item.displayAmount),
    ).toFixed(2);
  }

  get adjustmentProgress() {
    if (this.adjustmentsToProcess > 0) {
      return (this.adjustmentsProcessed / this.adjustmentsToProcess) * 100;
    }
    return 0;
  }

  @tracked
  reduceDialogOpen = false;

  @computed('filteredSelectedLineItems.@each.displayAmount')
  get maxReduceTo() {
    return Math.min(
      ...this.filteredSelectedLineItems.map((item) => item.displayAmount),
    ).toFixed(2);
  }

  @computed(
    'filteredSelectedLineItems.@each.{balance,displayAmount}',
    'adjustmentMode',
    'adjustBy',
    'adjustTo',
  )
  get reduceRows() {
    return this.filteredSelectedLineItems.filter((item) => {
      let diff = this.adjustBy;
      if (this.adjustmentMode === LineItemAdjustmentMode.TO) {
        diff = item.displayAmount - this.adjustTo;
      }
      return item.balance - diff >= 0;
    });
  }

  @setDiff('filteredSelectedLineItems', 'reduceRows')
  reduceExcludedRows;

  @tracked includeCurrent = true;

  @action
  setIncludeCurrent(value) {
    this.includeCurrent = value;
  }

  @tracked includeFormer = true;

  @action
  setIncludeFormer(value) {
    this.includeFormer = value;
  }

  @tracked refundsTotal;

  get enrolmentStatuses() {
    var enrolmentStatuses = [];
    if (this.includeCurrent) {
      enrolmentStatuses.push(EnrolmentStatus.CURRENT);
    }
    if (this.includeFormer) {
      enrolmentStatuses.push(EnrolmentStatus.FORMER);
    }
    return enrolmentStatuses;
  }

  @action
  toggleHideArchivedLineItems(value) {
    this.hideArchivedLineItems = !value;
    this.personOptions.setHideArchivedLineItems(!value);
  }

  // Group filtering actions.
  @action
  selectGroup(group) {
    this.groupFilter = [...this.groupFilter, group];
  }

  @action
  removeGroup(group) {
    // Reset selection whenever filters are changed
    this.selectedLineItems = [];
    this.groupFilter = this.groupFilter.filter((g) => g !== group);
  }

  // Year level filtering actions.
  @action
  selectYearLevel(year) {
    this.yearLevelFilter = [...this.yearLevelFilter, year];
  }

  @action
  removeYearLevel(year) {
    // Reset selection whenever filters are removed
    this.selectedLineItems = [];
    this.yearLevelFilter = this.yearLevelFilter.filter((y) => y !== year);
  }

  // Selection / bulk processing actions.
  @action
  toggleLineItem(item, selected) {
    if (selected) {
      this.selectedLineItems = [...this.selectedLineItems, item];
    } else {
      this.selectedLineItems = this.selectedLineItems.filter((i) => i !== item);
    }
  }

  @action
  toggleLineItems(items, selected) {
    if (selected) {
      this.selectedLineItems = [
        ...this.selectedLineItems,
        ...items.filter((item) => !item.isArchived),
      ];
    } else {
      this.selectedLineItems = this.selectedLineItems.filter(
        (i) => !items.includes(i),
      );
    }
  }

  @action
  openRefundDialog() {
    let refundType = 'direct';
    if (this.features.sapFinance) {
      refundType = 'account';
    }
    this.refundDialogOpen = true;
    this.transactionsLoading = true;
    this.refundAmount = this.maxRefundAmount;
    this.refundComment = '';
    this.refundDate = moment().format('YYYY-MM-DD');
    this.refundType = refundType;
    this.refundInProgress = false;
    this.refundsConfirmed = false;
    this.refundsProcessed = 0;
    this.refundsFailed = 0;
    this.refundErrors = [];
    this.refunds = [];
    // Update transactions to make sure we have the latest.
    this.model.transactions.update().then(() => {
      this.transactionsLoading = false;
    });
  }

  @action
  setRefundType(newType) {
    this.refundType = newType;
  }

  @action
  closeRefundDialog() {
    this.refundDialogOpen = false;
    this.refresh();
  }

  @action
  prepareRefunds() {
    this.refundInProgress = true;

    let refunds = this.refundRows.reduce((refunds, row) => {
      if (row.refundable <= 0) {
        // No refund possible.
        return refunds;
      }
      if (row.refundable > 0) {
        let amount = Math.min(row.refundable, this.refundAmount);
        let refund = {
          item: row.item,
          itemId: row.item.id,
          displayAmount: amount,
          data: {
            comment: this.refundComment,
            date: this.refundDate,
            amount: Math.round(amount * 100),
          },
        };
        if (this.refundType === 'account') {
          refund.data.accountId = parseInt(row.item.get('account.id'), 10);
        }
        refunds.push(refund);
      }
      return refunds;
    }, []);

    let refundsTotal = refunds.reduce((total, refund) => {
      return (total += refund.displayAmount);
    }, 0);
    refundsTotal = refundsTotal.toFixed(2);

    this.refunds = refunds;
    this.refundsTotal = refundsTotal;
    this.refundsToProcess = refunds.length;
  }

  @action
  processRefunds() {
    this.refundsConfirmed = true;

    let promise = Promise.resolve();
    this.refunds.forEach((refund) => {
      promise = promise.then(() => {
        let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/lineitems/${refund.itemId}/:refund`;
        let options = {
          data: JSON.stringify(refund.data),
        };
        return this.fetch
          .post(url, options)
          .catch((resp) => {
            this.incrementProperty('refundsFailed');

            let error = 'Refund failed';
            if (!isEmpty(resp.payload) && !isEmpty(resp.payload.message)) {
              error = resp.payload.message;
            } else if (!isEmpty(resp.message)) {
              error = resp.message;
            }
            this.refundErrors = [...this.refundErrors, { refund, error }];
          })
          .finally(() => {
            this.incrementProperty('refundsProcessed');
          });
      });
    });

    // Once done, refresh data to reflect the changes.
    promise.then(() => {
      this.refresh();
    });
  }

  @action
  openCancellationDialog() {
    this.cancellationDialogOpen = true;
    this.cancellationDialogOpen = true;
    this.cancellationComment = '';
    this.cancellationDate = moment().format('YYYY-MM-DD');
    this.cancellationType = 'writeoff';
    this.cancellationInProgress = false;
    this.cancellationsProcessed = 0;
    this.cancellationsFailed = 0;
    this.cancellationErrors = [];
  }

  @action
  closeCancellationDialog() {
    this.cancellationDialogOpen = false;
    this.refresh();
  }

  @action
  processCancellations() {
    this.cancellationInProgress = true;

    let promise = Promise.resolve();
    let action = 'void';
    if (this.cancellationType === 'writeoff') {
      action = 'writeoff';
    }

    this.cancellationRows.forEach((item) => {
      promise = promise.then(() => {
        let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/lineitems/${item.id}/:${action}`;
        let data = {
          lineItemId: parseInt(item.id, 10),
          comment: this.cancellationComment,
          date: this.cancellationDate,
        };
        let options = {
          data: JSON.stringify(data),
        };
        return this.fetch
          .post(url, options)
          .catch((resp) => {
            this.incrementProperty('cancellationsFailed');

            let error = 'Cancellation failed';
            if (!isEmpty(resp.payload) && !isEmpty(resp.payload.message)) {
              error = resp.payload.message;
            } else if (!isEmpty(resp.message)) {
              error = resp.message;
            }
            this.cancellationErrors = [
              ...this.cancellationErrors,
              { item, error },
            ];
          })
          .finally(() => {
            this.incrementProperty('cancellationsProcessed');
          });
      });
    });

    // Once done, refresh data to reflect the changes.
    promise.then(() => {
      this.refresh();
    });
  }

  @action
  openIncreaseDialog() {
    this.resetAdjustmentProperties();
    this.adjustmentMode = LineItemAdjustmentMode.INCREASE;
    this.adjustTo = this.minIncreaseTo;
    this.increaseDialogOpen = true;
  }

  @action
  closeIncreaseDialog() {
    this.increaseDialogOpen = false;
    this.refresh();
  }

  @action
  openReduceDialog() {
    this.resetAdjustmentProperties();
    this.adjustmentMode = LineItemAdjustmentMode.REDUCE;
    this.adjustTo = this.maxReduceTo;
    this.reduceDialogOpen = true;
  }

  @action
  closeReduceDialog() {
    this.reduceDialogOpen = false;
    this.refresh();
  }

  @action
  processAdjustments(lineItems) {
    this.adjustmentsInProgress = true;
    this.adjustmentsToProcess = this.filteredSelectedLineItems.length;
    if (this.reduceDialogOpen) {
      this.adjustmentsToProcess = this.reduceRows.length;
    }

    let promise = Promise.resolve();
    lineItems.forEach((item) => {
      promise = promise.then(() => {
        return this.finance
          .adjustLineItem(
            item,
            this.adjustmentComment,
            this.adjustmentDate,
            this.adjustmentMode,
            this.adjustTo,
            this.adjustBy,
          )
          .catch((resp) => {
            this.incrementProperty('adjustmentsFailed');

            let error = 'Adjustment failed';
            if (!isEmpty(resp.payload) && !isEmpty(resp.payload.message)) {
              error = resp.payload.message;
            } else if (!isEmpty(resp.message)) {
              error = resp.message;
            }
            this.adjustmentErrors = [...this.adjustmentErrors, { item, error }];
          })
          .finally(() => {
            this.incrementProperty('adjustmentsProcessed');
          });
      });
    });

    // Once done, refresh data to reflect the changes.
    promise.then(() => {
      this.refresh();
    });
  }

  resetAdjustmentProperties() {
    this.adjustBy = 0;
    this.adjustmentComment = '';
    this.adjustmentDate = moment().format('YYYY-MM-DD');
    this.adjustmentsInProgress = false;
    this.adjustmentsToProcess = 0;
    this.adjustmentsProcessed = 0;
    this.adjustmentsFailed = 0;
    this.adjustmentErrors = [];
  }

  refresh() {
    this.model.lineItems.update();
    this.model.transactions.update();
  }
}
